import type { Messages } from "@/i18n/locales/en";
import { legalMessagesPl } from "@/i18n/locales/legalPlData";
import { pagesMessagesPl } from "@/i18n/locales/pagesPlData";

export const plMessages = {
  meta: {
    description:
      "WasOK — skanuj produkty, zapisuj oceny i notatki, pamiętaj, co warto było kupić.",
    title: "WasOK — Pamiętaj, co warto było kupić",
  },
  header: {
    navFeatures: "Funkcje",
    navHowItWorks: "Jak to działa",
    navPrivacy: "Prywatność",
    navUseCases: "Zastosowania",
    download: "Pobierz",
    openMenu: "Otwórz menu",
    closeMenu: "Zamknij menu",
    languageAria: "Język",
  },
  hero: {
    badge: "📱 Już dostępne na Androida",
    title: "Pamiętaj, co warto było kupić.",
    subtitle:
      "Skanuj produkty, zapisuj oceny i notatki — i od razu przypomnij sobie, czy produkt naprawdę był dobry.",
    googlePlay: "Pobierz z Google Play",
    watchDemo: "Zobacz demo",
    privacyLine: "Wszystkie dane zostają na Twoim urządzeniu.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Skanuj, by pamiętać",
    snackCard: "Pyszne!",
    coffeeCard: "Za gorzkie",
    scrollHint: "Przewiń, by odkryć",
  },
  problems: {
    heading: "Brzmi znajomo?",
    subheading:
      "Wszyscy to znamy — stoisz przed półką i próbujesz sobie przypomnieć, czy ostatnio Ci smakowało.",
    notedAgo: "✓ Zanotowałeś to 3 miesiące temu",
    items: [
      {
        question: "Którą czekoladę naprawdę lubiłem?",
        note: "Idealna słodycz",
      },
      {
        question: "Dlaczego przestałem kupować ten szampon?",
        note: "Wysuszał włosy",
      },
      {
        question: "Czy ta kawa była dobra, czy rozczarowująca?",
        note: "Za gorzka",
      },
    ],
  },
  howItWorks: {
    heading: "Jak to działa",
    subheading:
      "Cztery proste kroki, by nigdy więcej nie zapomnieć swoich opinii o produktach.",
    stepPrefix: "Krok",
    steps: [
      {
        title: "Zeskanuj dowolny kod kreskowy",
        description:
          "Szybkie i łatwe skanowanie kamerą. Działa z każdym kodem kreskowym.",
      },
      {
        title: "Zapisz oceny, komentarze i zdjęcia",
        description:
          "Oceniaj produkty, dodawaj notatki, zdjęcia oraz ceny i lokalizacje.",
      },
      {
        title: "Natychmiast przypomnij sobie wrażenia",
        description:
          "Zeskanuj ten sam produkt później i zobacz wcześniejszą ocenę oraz notatki.",
      },
      {
        title: "Twórz inteligentne listy zakupów",
        description:
          "Twórz listy z produktów, którym już ufasz i które lubisz.",
      },
    ],
  },
  privacy: {
    heading: "Twoja historia zakupów zostaje Twoja.",
    body: "WasOK pomaga pamiętać produkty, które wypróbowałeś, przechowując oceny, notatki, zdjęcia i listy zakupów lokalnie na Twoim urządzeniu.",
    trustBanner:
      "💚 Zbudowane wokół Twojej historii zakupów — nie wokół zbierania Twoich danych.",
    bullets: [
      "Bez wymagania konta",
      "Bez wymaganej synchronizacji w chmurze",
      "Przechowywanie lokalne",
      "Kopie zapasowe pod Twoją kontrolą",
      "Opcjonalne wyszukiwanie kodów kreskowych dla informacji o produktach.",
      "Finansowane reklamami z Google AdMob",
    ],
  },
  features: {
    heading: "Wszystko, czego potrzebujesz",
    subheading:
      "Potężne funkcje zaprojektowane, by śledzenie produktów było proste i prywatne.",
    items: [
      {
        title: "Skaner kodów kreskowych",
        description: "Szybkie i dokładne skanowanie kodów kreskowych dowolnych produktów.",
      },
      {
        title: "Oceny produktów",
        description: "Oceniaj produkty od 1 do 5 gwiazdek, by pamiętać jakość.",
      },
      {
        title: "Zdjęcia produktów",
        description: "Dołączaj zdjęcia, by pamiętać opakowanie lub szczegóły.",
      },
      {
        title: "Kategorie",
        description: "Organizuj produkty we własne kategorie.",
      },
      {
        title: "Śledzenie sklepów",
        description: "Pamiętaj, gdzie kupiłeś każdy produkt.",
      },
      {
        title: "Zapisywanie cen",
        description: "Śledź ceny, by znaleźć najlepsze oferty.",
      },
      {
        title: "Listy zakupów",
        description: "Twórz listy z ulubionych produktów.",
      },
      {
        title: "Dostęp offline",
        description: "Działa doskonale bez połączenia z internetem.",
      },
      {
        title: "Pamięć lokalna",
        description: "Wszystkie dane bezpiecznie na Twoim urządzeniu.",
      },
    ],
  },
  useCases: {
    heading: "Idealne na każdą półkę",
    subheading:
      "Czy kupujesz jedzenie, kosmetyki, czy artykuły domowe — WasOK pomaga Ci pamiętać.",
    cards: [
      {
        emoji: "🍫",
        title: "Przekąski i słodycze",
        description: "Pamiętaj, które słodycze naprawdę były dobre.",
      },
      {
        emoji: "☕",
        title: "Kawa i napoje",
        description: "Śledź kawy warte ponownego zakupu.",
      },
      {
        emoji: "💄",
        title: "Kosmetyki i pielęgnacja",
        description: "Notuj reakcje skóry.",
      },
      {
        emoji: "🧹",
        title: "Artykuły domowe",
        description: "Unikaj ponownego kupowania produktów, które zawiodły.",
      },
    ],
  },
  screenshots: {
    heading: "Piękne i intuicyjne",
    subheading:
      "Przemyślany interfejs, który sprawia, że śledzenie produktów to przyjemność.",
    items: [
      { title: "Skaner" },
      { title: "Szczegóły produktu" },
      { title: "Wprowadzanie oceny" },
      { title: "Wyszukiwanie i historia" },
      { title: "Lista zakupów" },
    ],
  },
  cta: {
    heading: "Pamięć zakupowa do prawdziwego życia.",
    body: "Przestań zapominać, które produkty warto było kupić. Zacznij kupować mądrzej już dziś.",
    googlePlay: "Pobierz z Google Play",
    scanQr: "Zeskanuj, by pobrać",
    tagline: "Prywatne · Offline · Proste",
  },
  footer: {
    tagline: "Pamiętaj, co warto było kupić.",
    linksHeading: "Linki",
    privacyPolicy: "Polityka prywatności",
    terms: "Warunki korzystania",
    contact: "Kontakt",
    linkFaq: "FAQ",
    linkLicenses: "Licencje open source",
    linkAdvertising: "Reklamy i monetyzacja",
    downloadHeading: "Pobierz",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Wszelkie prawa zastrzeżone. Stworzone z 💚 dla mądrzejszych zakupów.",
  },
  legal: legalMessagesPl,
  faq: pagesMessagesPl.faq,
  support: pagesMessagesPl.support,
  advertising: pagesMessagesPl.advertising,
} satisfies Messages;
